import type { useTranslations } from 'next-intl';
import type { AppUiDetails, AppUiDetailsConfig } from '../type/AppUiDetails';
import type { WorkatoApp, WorkatoAppRawItem } from '../type/workato';

/**
 * Generate the UI values to be inserted into the template for a Workato powered app.
 */
export function getAppUiDetails(
  workatorConnection: WorkatoApp,
  workatoAppUIDetails: ReadonlyArray<AppUiDetailsConfig>,
  t: ReturnType<typeof useTranslations>,
): AppUiDetails {
  const uiConfig = findAppUiDetailsConfig(
    workatorConnection,
    workatoAppUIDetails,
  );

  const name = uiConfig?.name || workatorConnection.name;
  const description =
    uiConfig?.description || t('appDescription', { app_name: name });
  const icon = uiConfig?.icon || 'https://public.otter.ai/img/icon-puzzle.svg';
  const showNewBadge = !!uiConfig?.showNewBadge;
  const learnMoreLink =
    uiConfig?.learnMoreLink || 'https://otter.ai/integrations';
  const canMemberConnect = !!uiConfig?.canMemberConnect;

  return {
    name,
    description,
    icon,
    showNewBadge,
    learnMoreLink,
    canMemberConnect,
  };
}

/**
 * Matches the Workato app data with our custom UI settings.
 */
function findAppUiDetailsConfig(
  workatorConnection: WorkatoApp,
  workatoAppUIDetails: ReadonlyArray<AppUiDetailsConfig>,
): AppUiDetailsConfig | null {
  const foundById = workatoAppUIDetails.find(
    (appUiDetails) => workatorConnection.id === appUiDetails.lookup.id,
  );
  if (foundById) {
    return foundById;
  }

  const foundByProviderPrefix = workatoAppUIDetails.find(
    (appUiDetails) =>
      appUiDetails.lookup.provider &&
      workatorConnection.provider.startsWith(appUiDetails.lookup.provider),
  );

  return foundByProviderPrefix || null;
}

export function workatoAppUiDetails(
  config: WorkatoAppRawItem[],
  t: ReturnType<typeof useTranslations>,
): ReadonlyArray<AppUiDetailsConfig> {
  return config.map((item: WorkatoAppRawItem) => {
    let description = '';

    switch (item.providerPrefix) {
      case 'zoom_phone':
        description = t.raw('apps.zoom_phone.description');
        break;
      case 'outreach':
        description = t.raw('apps.outreach.description');
        break;
      case 'google_drive':
        description = t.raw('apps.google_drive.description');
        break;
    }

    return {
      name: item.name,
      icon: item.icon,
      description,
      showNewBadge: item.showNewBadge,
      canMemberConnect: item.canMemberConnect,
      lookup: {
        provider: item.providerPrefix,
      },
    };
  });
}
